import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn, fmtDate } from "../lib/format";
import { Loader, Empty, GlassPage, GlassHeader } from "../components/kit";

type ArchType = "products" | "customers" | "memos";

export default function Archive() {
  const [type, setType] = useState<ArchType>("products");
  const [list, setList] = useState<any[] | null>(null);

  const load = useCallback(() => {
    setList(null);
    api.get("/archived", { params: { type } }).then((r) => setList(r.data));
  }, [type]);
  useEffect(() => { load(); }, [load]);

  const restore = async (item: any) => {
    try {
      await api.post(`/${type}/${item.id}/restore`);
      toast.success("রিস্টোর হয়েছে");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tabBtn = (t: ArchType, label: string, tid: string) => (
    <button className={`inline-flex items-center h-9 px-4 rounded-lg text-sm font-semibold transition-all active:scale-95 ${type === t ? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25" : "bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10"}`} onClick={() => setType(t)} data-testid={tid}>{label}</button>
  );

  const head = type === "memos" ? "মোট / বাকি" : type === "products" ? "স্টক / দর" : "বাকি";

  return (
    <GlassPage testid="archive-page">
      <GlassHeader title="আর্কাইভ" subtitle="মুছে ফেলা আইটেম এখানে থাকে — দরকার হলে রিস্টোর করুন" />
      <div className="flex gap-2 mb-4">
        {tabBtn("products", "পণ্য", "archive-tab-products")}
        {tabBtn("customers", "ক্রেতা", "archive-tab-customers")}
        {tabBtn("memos", "মেমো", "archive-tab-memos")}
      </div>
      <div className="glass-card overflow-x-auto">
        {!list ? <Loader /> : list.length === 0 ? <Empty text="আর্কাইভে কিছু নেই" /> : (
          <table className="w-full text-sm" data-testid="archive-table">
            <thead><tr className="text-slate-400 text-left border-b border-white/10"><th className="px-5 py-3 font-medium">{type === "memos" ? "মেমো" : "নাম"}</th><th className="px-3 py-3 font-medium text-right">{head}</th><th className="px-5 py-3" /></tr></thead>
            <tbody>
              {list.map((it) => (
                <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`archive-row-${it.id}`}>
                  <td className="px-5 py-3 font-semibold text-white">
                    {type === "memos" ? it.invoice_no : it.name}
                    <span className="block text-xs text-slate-400">{type === "products" ? it.company : type === "customers" ? bn(it.mobile) : `${it.customer?.name || ""} • ${fmtDate(it.date)}`}</span>
                  </td>
                  <td className="px-3 py-3 text-right num text-slate-300">
                    {type === "products" ? `${bn(it.stock)} ${it.unit} / ${tk(it.rate)}` : type === "customers" ? tk(it.due) : `${tk(it.grand_total)} / ${tk(it.current_due)}`}
                  </td>
                  <td className="px-5 py-3 text-right"><button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 text-sm font-semibold transition-all active:scale-95" onClick={() => restore(it)} data-testid={`archive-restore-${it.id}`}><RotateCcw size={15} /> রিস্টোর</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </GlassPage>
  );
}
